import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Badge, Form } from "react-bootstrap";
import { FaEdit, FaTrash, FaExchangeAlt, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import ChequeEstadoModal from "./ChequeEstadoModal";
import ChequeDeleteModal from "./ChequeDeleteModal";
import { getCheques } from "../../../Api/ChequesApi";

const ITEMS_PER_PAGE = 20;

const formatFecha = (fecha) => {
  if (!fecha) return "";
  return String(fecha).split("T")[0];
};

const Cheques = () => {
  const navigate = useNavigate();

  const [cheques, setCheques] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    nombre: "",
    fecha: "",
    monto: "",
    estado: "",
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [showEstado, setShowEstado] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);

  const cargarCheques = async () => {
    const data = await getCheques();
    setCheques(data || []);
  };

  useEffect(() => {
    cargarCheques();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters]);

  const chequesFiltrados = useMemo(() => {
    return cheques.filter((c) => {
      const globalMatch =
        c.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        String(c.monto).includes(search) ||
        c.estado?.toLowerCase().includes(search.toLowerCase()) ||
        String(c.chequenumero).includes(search);

      const columnMatch =
        c.nombre?.toLowerCase().includes(filters.nombre.toLowerCase()) &&
        String(c.fechaemision).includes(filters.fecha) &&
        String(c.monto).includes(filters.monto) &&
        c.estado?.toLowerCase().includes(filters.estado.toLowerCase());

      return globalMatch && columnMatch;
    });
  }, [cheques, search, filters]);

  const totalPages = Math.ceil(chequesFiltrados.length / ITEMS_PER_PAGE);

  const chequesPaginados = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return chequesFiltrados.slice(start, start + ITEMS_PER_PAGE);
  }, [chequesFiltrados, currentPage]);

  const badgeEstado = (estado) => {
    switch (estado) {
      case "CREADO":
        return <Badge bg="secondary">CREADO</Badge>;
      case "IMPRESO":
        return <Badge bg="primary">IMPRESO</Badge>;
      case "ENTREGADO":
        return <Badge bg="success">ENTREGADO</Badge>;
      case "ANULADO":
        return <Badge bg="danger">ANULADO</Badge>;
      default:
        return <Badge bg="dark">{estado}</Badge>;
    }
  };

  const puedeCambiarEstado = (estado) =>
    estado === "CREADO" || estado === "IMPRESO" || estado === "ENTREGADO";
  const puedeEliminar = (estado) => estado === "CREADO";

  return (
    <>
      {/* ================= HEADER ================= */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Gestión de Cheques</h4>
        <Button onClick={() => navigate("/cheques/nuevo")}>
          <FaPlus className="me-2" />
          Nuevo Cheque
        </Button>
      </div>

      {/* ================= BUSCADOR ================= */}
      <Form.Control
        className="mb-3"
        placeholder="Buscar cheque..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* ================= TABLA ================= */}
      <Table striped responsive size="sm">
        <thead>
          <tr>
            <th>#</th>
            <th>N° Cheque</th>
            <th>Beneficiario</th>
            <th>Fecha</th>
            <th>Monto</th>
            <th>Estado</th>
            <th width="180">Opciones</th>
          </tr>

          <tr>
            <th />
            <th />
            <th>
              <Form.Control
                size="sm"
                placeholder="Filtrar..."
                value={filters.nombre}
                onChange={(e) =>
                  setFilters({ ...filters, nombre: e.target.value })
                }
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                placeholder="YYYY-MM-DD"
                value={filters.fecha}
                onChange={(e) =>
                  setFilters({ ...filters, fecha: e.target.value })
                }
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                placeholder="Monto"
                value={filters.monto}
                onChange={(e) =>
                  setFilters({ ...filters, monto: e.target.value })
                }
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                placeholder="Estado"
                value={filters.estado}
                onChange={(e) =>
                  setFilters({ ...filters, estado: e.target.value })
                }
              />
            </th>
            <th />
          </tr>
        </thead>

        <tbody>
          {chequesPaginados.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No hay cheques
              </td>
            </tr>
          )}

          {chequesPaginados.map((c) => (
            <tr key={c.idcheque}>
              <td>{c.idcheque}</td>
              <td>{c.chequenumero}</td>
              <td>{c.nombre}</td>
              <td>{formatFecha(c.fechaemision)}</td>
              <td>{Number(c.monto).toLocaleString()}</td>
              <td>{badgeEstado(c.estado)}</td>
              <td className="text-center">
                {/* EDITAR → PÁGINA */}
                <Button
                  size="sm"
                  variant="primary"
                  className="me-1"
                  title="Ver / Editar"
                  onClick={() => navigate(`/cheques/editar/${c.idcheque}`)}
                >
                  <FaEdit />
                </Button>

                {puedeCambiarEstado(c.estado) && (
                  <Button
                    size="sm"
                    variant="info"
                    className="me-1"
                    title="Cambiar estado"
                    onClick={() => {
                      setSelectedCheque(c);
                      setShowEstado(true);
                    }}
                  >
                    <FaExchangeAlt />
                  </Button>
                )}

                {puedeEliminar(c.estado) && (
                  <Button
                    size="sm"
                    variant="danger"
                    title="Eliminar"
                    onClick={() => {
                      setSelectedCheque(c);
                      setShowDelete(true);
                    }}
                  >
                    <FaTrash />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* ================= PAGINACIÓN ================= */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between mt-3">
          <div>
            Página {currentPage} de {totalPages}
          </div>
          <div>
            <Button
              size="sm"
              className="me-2"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* ================= MODALES ================= */}
      <ChequeEstadoModal
        show={showEstado}
        onHide={() => {
          setShowEstado(false);
          setSelectedCheque(null);
        }}
        cheque={selectedCheque}
        onSaved={cargarCheques}
      />

      <ChequeDeleteModal
        show={showDelete}
        onHide={() => {
          setShowDelete(false);
          setSelectedCheque(null);
        }}
        cheque={selectedCheque}
        onDeleted={cargarCheques}
      />
    </>
  );
};

export default Cheques;
